import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import builderQuery, { QueryOptions } from 'odata-query';
import { map, Observable } from 'rxjs';
import { GstarClient } from '../../../web-api-client';
import { mapResidencyJurisdictionFromResponse } from '../mappers';
import { mapResidencyJurisdictionToCreateRequest } from '../mappers/residency-jurisdiction/map-residency-jurisdiction-to-create-request';
import { mapResidencyJurisdictionToUpdateRequest } from '../mappers/residency-jurisdiction/map-residency-jurisdiction-to-update-request';
import { Collection, ResidencyJurisdiction } from '../models';

@Injectable({
  providedIn: 'root',
})
export class ResidencyJurisdictionService {
  private readonly gstarClient = inject(GstarClient);

  selectedJurisdiction: WritableSignal<ResidencyJurisdiction | undefined> =
    signal(undefined);

  get(
    query?: Partial<QueryOptions<ResidencyJurisdiction>>
  ): Observable<Collection<ResidencyJurisdiction>> {
    let orderBy: string | undefined = undefined;
    if (Array.isArray(query?.orderBy)) {
      orderBy = query!.orderBy.join(',');
    } else if (typeof query?.orderBy === 'string') {
      orderBy = query.orderBy;
    }

    let filterStr: string | undefined;
    if (query?.filter) {
      filterStr = builderQuery({ filter: query.filter }).replace(
        '?$filter=',
        ''
      );
    }

    return this.gstarClient
      .residencyjurisdictionsGET(
        undefined,
        filterStr,
        query?.top?.toString(),
        query?.skip?.toString(),
        orderBy,
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response =>
              mapResidencyJurisdictionFromResponse(response)
            ) ?? [],
        }))
      );
  }

  getById(residencyJurisdictionId: number): Observable<ResidencyJurisdiction> {
    return this.gstarClient
      .residencyjurisdictionsGET2(residencyJurisdictionId)
      .pipe(map(response => mapResidencyJurisdictionFromResponse(response)));
  }

  create(
    residencyJurisdiction: ResidencyJurisdiction
  ): Observable<ResidencyJurisdiction> {
    return this.gstarClient
      .residencyjurisdictionsPOST(
        mapResidencyJurisdictionToCreateRequest(residencyJurisdiction)
      )
      .pipe(map(response => mapResidencyJurisdictionFromResponse(response)));
  }

  update(residencyJurisdiction: ResidencyJurisdiction): Observable<void> {
    return this.gstarClient.residencyjurisdictionsPUT(
      residencyJurisdiction.residencyJurisdictionId,
      mapResidencyJurisdictionToUpdateRequest(residencyJurisdiction)
    );
  }

  selectResidencyJurisdiction(jurisdiction: ResidencyJurisdiction): void {
    this.selectedJurisdiction.set(jurisdiction);
  }
}
